import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Butterfly from '../Classes/Butterfly.tsx';
import ButterflyFamily from '../Classes/ButterflyFamily.tsx';
import ButterflySubfamily from '../Classes/ButterflySubfamily.tsx';
import { T, US_STATES, familyColor } from '../tokens';

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill={filled ? '#A8462B' : 'none'}>
    <path
      d="M12 21C12 21 3 14 3 8a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6-9 13-9 13z"
      stroke={filled ? '#A8462B' : '#9A9587'}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M16.5 16.5l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────────

type ViewMode = 'all' | 'favorites';
type GroupMode = 'flat' | 'family';
type NameMode = 'common' | 'scientific';

// ─── Species row ──────────────────────────────────────────────────────────────

function SpeciesRow({
  butterfly,
  isFavorite,
  onToggle,
}: {
  butterfly: Butterfly;
  isFavorite: boolean;
  onToggle: (id: number) => void;
}) {
  const displayName = butterfly.commonName || butterfly.scientificName;
  const sciName = butterfly.scientificName !== 'Unknown' ? butterfly.scientificName : null;
  const isUnknownSpecies = displayName === 'Unknown';
  const unknownFamilyNames = [butterfly.commonFamilyName || 'Unknown', butterfly.scientificFamilyName || 'Unknown'];
  const unknownSubfamilyNames = [butterfly.commonSubfamilyName || 'Unknown', butterfly.scientificSubfamilyName || 'Unknown'];
  const fColor = familyColor(butterfly.commonFamilyName || butterfly.scientificFamilyName || '');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 16px',
      borderBottom: `1px solid ${T.line}`,
    }}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        background: fColor, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: T.font, fontSize: 14, fontWeight: 500, color: T.ink,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {displayName}
        </div>
        {sciName && (
          <div style={{
            fontFamily: T.fontMono, fontSize: 11, color: T.ink3,
            fontStyle: 'italic', marginTop: 1,
          }}>
            {sciName}
          </div>
        )}
        {isUnknownSpecies && (
          <>
            <div style={{
              fontFamily: T.font, fontSize: 11, color: T.ink3,
              marginTop: 2,
            }}>
              {unknownFamilyNames.join(' / ')}
            </div>
            <div style={{
              fontFamily: T.font, fontSize: 11, color: T.ink3,
              marginTop: 2,
            }}>
              {unknownSubfamilyNames.join(' / ')}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => onToggle(butterfly.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 4, flexShrink: 0, display: 'flex', alignItems: 'center',
        }}
      >
        <HeartIcon filled={isFavorite} />
      </button>
    </div>
  );
}

// ─── Family section ───────────────────────────────────────────────────────────

function FamilySection({
  family,
  favorites,
  onToggle,
}: {
  family: ButterflyFamily;
  favorites: Map<number, boolean>;
  onToggle: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const fColor = familyColor(family.commonName || family.scientificName);

  const allSpecies = family.subfamilyList.flatMap(sf => sf.speciesList);
  const favCount = allSpecies.filter(b => favorites.get(b.id)).length;

  return (
    <div style={{ marginBottom: 6 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: T.card,
          border: `1px solid ${T.line}`, borderRadius: open ? `${T.r.md}px ${T.r.md}px 0 0` : T.r.md,
          padding: '12px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: T.shadowCard,
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: T.r.sm, flexShrink: 0,
          background: fColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <HeartIcon filled={false} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.ink }}>
            {family.commonName}
          </div>
          <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>
            {family.scientificName}
          </div>
        </div>
        {favCount > 0 && (
          <div style={{
            fontFamily: T.font, fontSize: 11, fontWeight: 600,
            color: T.rust, background: '#FAE8E5',
            borderRadius: T.r.pill, padding: '2px 8px',
          }}>
            {favCount} ♥
          </div>
        )}
        <span style={{ color: T.ink3 }}><ChevronDown open={open} /></span>
      </button>

      {open && (
        <div style={{
          border: `1px solid ${T.line}`, borderTop: 'none',
          borderRadius: `0 0 ${T.r.md}px ${T.r.md}px`,
          overflow: 'hidden', background: T.card,
          boxShadow: T.shadowCard,
        }}>
          {family.subfamilyList.map(sf => (
            <SubfamilySection key={sf.scientificName} subfamily={sf} favorites={favorites} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubfamilySection({
  subfamily,
  favorites,
  onToggle,
}: {
  subfamily: ButterflySubfamily;
  favorites: Map<number, boolean>;
  onToggle: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);

  if (subfamily.speciesList.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: T.bgDeep,
          border: 'none', borderBottom: `1px solid ${T.line}`,
          padding: '8px 16px 8px 20px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.ink2 }}>
          {subfamily.commonName}
        </span>
        <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>
          {subfamily.scientificName}
        </span>
        <span style={{ marginLeft: 'auto', color: T.ink3 }}><ChevronDown open={open} /></span>
      </button>

      {open && subfamily.speciesList.map(b => (
        <SpeciesRow
          key={b.id}
          butterfly={b}
          isFavorite={!!favorites.get(b.id)}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const FavoriteButterflies = () => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState<number>();
  const [butterflies, setButterflies] = useState<Butterfly[]>([]);
  const [butterflyFamilies, setButterflyFamilies] = useState<ButterflyFamily[]>([]);

  const [prevFavorites, setPrevFavorites] = useState<Map<number, boolean>>(new Map());
  const [favorites, setFavorites] = useState<Map<number, boolean>>(new Map());
  const [addFavorites, setAddFavorites] = useState<number[]>([]);
  const [removeFavorites, setRemoveFavorites] = useState<number[]>([]);

  const [view, setView] = useState<ViewMode>('all');
  const [group, setGroup] = useState<GroupMode>('flat');
  const [nameMode] = useState<NameMode>('common');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const fetchButterflies = (uid: number) => {
    fetch('api/butterflies')
      .then(r => r.json())
      .then((data: any[]) => {
        const list = data.map(d => new Butterfly(
          d.id, d.commonName, d.scientificName,
          d.commonFamilyName, d.scientificFamilyName,
          d.commonSubfamilyName, d.scientificSubfamilyName, d.states,
        ));
        const favMap = new Map<number, boolean>();
        list.forEach(b => favMap.set(b.id, false));
        setButterflies(list);
        fetchFavorites(favMap, uid);
      })
      .catch(err => console.error('Error fetching butterflies:', err));
  };

  const fetchFavorites = (base: Map<number, boolean>, uid: number) => {
    fetch(`api/butterflies/favorites/${uid}`)
      .then(r => r.json())
      .then((data: any[]) => {
        const favMap = new Map(base);
        data.forEach(d => favMap.set(d.butterfly.id, true));
        setFavorites(favMap);
        setPrevFavorites(favMap);
      })
      .catch(err => console.error('Error fetching favorites:', err));
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.userId) {
      setUserId(user.userId);
      fetchButterflies(user.userId);
    } else {
      navigate('/');
    }
  }, []);

  // Build family tree once butterflies load
  useEffect(() => {
    if (butterflies.length === 0) return;
    const families: ButterflyFamily[] = [];
    butterflies.forEach(b => {
      let fam = families.find(f => f.scientificName === b.scientificFamilyName);
      if (!fam) { fam = new ButterflyFamily(b.commonFamilyName, b.scientificFamilyName); families.push(fam); }
      let sf = fam.subfamilyList.find(s => s.scientificName === b.scientificSubfamilyName);
      if (!sf) { sf = new ButterflySubfamily(b.commonSubfamilyName, b.scientificSubfamilyName); fam.subfamilyList.push(sf); }
      sf.speciesList.push(b);
    });
    setButterflyFamilies(families);
  }, [butterflies]);

  // ── Filtering ────────────────────────────────────────────────────────────────

  const q = search.trim().toLowerCase();

  const matchesButterfly = (b: Butterfly) => {
    if (stateFilter && !(b.states || '').includes(stateFilter)) return false;
    if (view === 'favorites' && !favorites.get(b.id)) return false;
    if (q) {
      const name = nameMode === 'common' ? b.commonName : b.scientificName;
      if (!name.toLowerCase().includes(q)) return false;
    }
    return true;
  };

  const filteredFlat = useMemo(() =>
    butterflies.filter(matchesButterfly),
    [butterflies, view, favorites, stateFilter, search, nameMode]
  );

  const filteredFamilies = useMemo(() => {
    if (group !== 'family') return [];
    return butterflyFamilies
      .map(fam => {
        const subfamilies = fam.subfamilyList
          .map(sf => {
            const species = sf.speciesList.filter(matchesButterfly);
            return { ...sf, speciesList: species };
          })
          .filter(sf => sf.speciesList.length > 0);
        return { ...fam, subfamilyList: subfamilies };
      })
      .filter(fam => fam.subfamilyList.length > 0);
  }, [butterflyFamilies, view, favorites, stateFilter, search, nameMode, group]);

  // ── Favorite toggle ──────────────────────────────────────────────────────────

  const toggleFavorite = (id: number) => {
    const wasFav = !!favorites.get(id);
    setFavorites(prev => new Map(prev).set(id, !wasFav));
    if (wasFav) {
      setRemoveFavorites(prev => [...prev.filter(x => x !== id), id]);
      setAddFavorites(prev => prev.filter(x => x !== id));
    } else {
      setAddFavorites(prev => [...prev.filter(x => x !== id), id]);
      setRemoveFavorites(prev => prev.filter(x => x !== id));
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleSave = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    try {
      await Promise.all([
        ...addFavorites
          .filter(id => !prevFavorites.get(id))
          .map(id => fetch(`api/butterflies/favorites?userId=${userId}&butterflyId=${id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          })),
        ...removeFavorites
          .filter(id => prevFavorites.get(id))
          .map(id => fetch(`api/butterflies/favorites?userId=${userId}&butterflyId=${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          })),
      ]);
      setSaved(true);
      setTimeout(() => navigate('/menu'), 800);
    } catch (err) {
      console.error('Error saving favorites:', err);
    } finally {
      setSaving(false);
    }
  };

  const pendingChanges = addFavorites.filter(id => !prevFavorites.get(id)).length
    + removeFavorites.filter(id => prevFavorites.get(id)).length;

  const favCount = Array.from(favorites.values()).filter(Boolean).length;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="lep-page">
      <form onSubmit={handleSave} style={{ display: 'contents' }}>
        <div className="lep-page-inner" style={{ padding: '0 0 80px' }}>

          {/* Sticky header */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 10,
            background: T.bg, borderBottom: `1px solid ${T.line}`,
            padding: '54px 20px 0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <button
                type="button"
                onClick={() => navigate('/menu')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink2, padding: 4, margin: -4, display: 'flex' }}
              >
                <ArrowLeft />
              </button>
              <div style={{
                flex: 1, fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 600,
                color: T.ink, letterSpacing: '-0.02em',
              }}>
                Favorites
              </div>
              {favCount > 0 && (
                <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink3 }}>
                  {favCount} saved
                </div>
              )}
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search species…"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  height: 40, paddingLeft: 36, paddingRight: 12,
                  fontFamily: T.font, fontSize: 15, color: T.ink,
                  background: T.bgDeep, border: `1px solid ${T.line}`,
                  borderRadius: T.r.md, outline: 'none',
                }}
              />
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.ink3, pointerEvents: 'none' }}>
                <SearchIcon />
              </span>
            </div>

            {/* Tabs */}
            <div className="lep-seg" style={{ borderRadius: `${T.r.md}px ${T.r.md}px 0 0` }}>
              <button type="button" className={`lep-seg-btn${view === 'all' ? ' active' : ''}`} onClick={() => setView('all')}>
                All species
              </button>
              <button type="button" className={`lep-seg-btn${view === 'favorites' ? ' active' : ''}`} onClick={() => setView('favorites')}>
                Favorites
                {favCount > 0 && (
                  <span style={{ marginLeft: 5, fontFamily: T.fontMono, fontSize: 11, opacity: view === 'favorites' ? 0.6 : 0.4 }}>
                    {favCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Sub-controls */}
          <div style={{ padding: '12px 20px 0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Group toggle */}
            <button
              type="button"
              onClick={() => setGroup(g => g === 'flat' ? 'family' : 'flat')}
              style={{
                fontFamily: T.font, fontSize: 12, fontWeight: 500,
                color: group === 'family' ? T.forest : T.ink2,
                background: group === 'family' ? T.forestSoft : T.bgDeep,
                border: `1px solid ${group === 'family' ? T.forest + '40' : T.line}`,
                borderRadius: T.r.pill, padding: '5px 12px', cursor: 'pointer',
              }}
            >
              {group === 'family' ? '✓ ' : ''}By family
            </button>

            {/* State filter */}
            <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
              <select
                value={stateFilter}
                onChange={e => setStateFilter(e.target.value)}
                style={{
                  width: '100%', height: 34, paddingLeft: 10, paddingRight: 24,
                  fontFamily: T.font, fontSize: 12, color: stateFilter ? T.ink : T.ink3,
                  background: stateFilter ? T.honeySoft : T.bgDeep,
                  border: `1px solid ${stateFilter ? T.honey : T.line}`,
                  borderRadius: T.r.pill, outline: 'none', appearance: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="">All states</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.ink3, fontSize: 10 }}>▾</span>
            </div>
          </div>

          {/* List */}
          <div style={{ padding: '12px 20px 0' }}>
            {group === 'flat' ? (
              filteredFlat.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: T.font, fontSize: 15, color: T.ink3 }}>
                  {view === 'favorites' ? 'No favorites yet — tap ♥ on any species' : 'No species match'}
                </div>
              ) : (
                <div style={{
                  background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: T.r.lg, overflow: 'hidden', boxShadow: T.shadowCard,
                }}>
                  {filteredFlat.map(b => (
                    <SpeciesRow
                      key={b.id}
                      butterfly={b}
                      isFavorite={!!favorites.get(b.id)}
                      onToggle={toggleFavorite}
                    />
                  ))}
                  {/* Remove last row's border */}
                </div>
              )
            ) : (
              filteredFamilies.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: T.font, fontSize: 15, color: T.ink3 }}>
                  {view === 'favorites' ? 'No favorites yet — tap ♥ on any species' : 'No species match'}
                </div>
              ) : (
                filteredFamilies.map(fam => (
                  <FamilySection
                    key={fam.scientificName}
                    family={fam as ButterflyFamily}
                    favorites={favorites}
                    onToggle={toggleFavorite}
                  />
                ))
              )
            )}
          </div>
          {/* Sticky save footer*/}
            <div style={{
            position: 'sticky', bottom: 0, left: 0, right: 0,
            padding: '16px 20px 16px', background: T.bg,
            borderTop: `1px solid ${T.line}`,
            zIndex: 100
            }}>
            <button
                type="submit"
                disabled={saving}
                style={{
                width: '100%', height: 52, borderRadius: T.r.pill,
                background: saved ? '#2A7A35' : T.forest,
                border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: '#fff',
                letterSpacing: '-0.01em',
                boxShadow: '0 4px 16px rgba(45,80,22,0.25)',
                transition: 'background 0.2s',
                }}
            >
                {saved ? (
                <><CheckIcon /> Saved</>
                ) : saving ? (
                'Saving…'
                ) : pendingChanges > 0 ? (
                `Save ${pendingChanges} change${pendingChanges !== 1 ? 's' : ''}`
                ) : (
                'Done'
                )}
            </button>
            </div>
        </div>
      </form>
    </div>
  );
};

export default FavoriteButterflies;
